import { useLayoutEffect, useRef, useState } from "react";
import { MergeIcon } from "lucide-react";

import { FinancialAccountFragment } from "@/__generated__/graphql";
import { Buttons } from "@/components/atoms/Buttons";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations } from "@/constants";
import { removeNodesFromConnections } from "@/app/_/subscriptions/writeObjectChangeToCache";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useScope } from "@/hooks/useScope";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { Objects } from "@/objects";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";

import { Spinner } from "@/components/ui/spinner";

export const COMMAND_ID = "merge-bank-accounts";

export type MergeBankAccountsContextType = {
  /** The duplicate: the account merged from, which is removed. */
  sourceIdentifier: string;
  sourceName?: string | null;
  /**
   * The entity banking at this account. An account belongs to its entity's
   * books, so the one it is folded into has to be the same entity's - the
   * server refuses anything else, and this is what keeps it off the list.
   */
  entityIdentifier?: string | null;
};

/**
 * Two accounts that are one account, made one.
 *
 * A bank sends an account under the name it keeps for it and a statement
 * prints another, so the same money can end up in the books twice - once
 * per name - and neither balance is the account's own. This picks the one
 * to keep and folds the other into it.
 */
function Component({
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId?: string;
  context?: MergeBankAccountsContextType;
}) {
  const clearCommandStack = State((store) => store.clearCommandStack);
  const setSnackbarMessage = State((store) => store.setSnackbarMessage);
  const { activeEntityIdentifiers } = useScope();

  const [target, setTarget] = useState<FinancialAccountFragment | null>(null);
  const activeIndexRef = useRef<{ onClick: () => void }>({
    onClick: () => {},
  });

  const sourceIdentifier = context?.sourceIdentifier;
  const sourceName = context?.sourceName ?? "this account";
  // Only the entity's own accounts, not every entity in view.
  const entityIdentifiers = context?.entityIdentifier
    ? [context.entityIdentifier]
    : activeEntityIdentifiers;

  const { mutate: mergeAccounts, loading: isMerging } = useBackendMutation({
    mutation: Mutations.MERGE_FINANCIAL_ACCOUNTS,
    // The account merged from is gone, so it goes from the cache too - out of
    // every page of the accounts connection, count included, and then the
    // record itself - the same way a deleted row leaves when the server says
    // so. Left there it sits in the list until the page is reloaded.
    update: (cache) =>
      removeNodesFromConnections(
        cache,
        "financialAccountsConnection",
        [
          identifierAndTypenameToId(
            sourceIdentifier ?? "",
            "FinancialAccountObject",
          ),
        ],
        "FinancialAccountObject",
      ),
    // A report's figures are worked out on the server, so they are asked
    // again rather than mended here.
    refetchQueries: ["ReportWidgetContent"],
    callbacks: {
      onSuccess: () => {
        setSnackbarMessage(`${sourceName} is now part of ${target?.name}.`);
        clearCommandStack();
      },
    },
  });

  if (!target) {
    return (
      <StickyLayout
        fillHeight={false}
        stickyBottomChildren={
          <CommandCenterListFooter
            buttons={[
              {
                variant: "default",
                size: "sm",
                children: "Select",
                onClick: () => activeIndexRef.current.onClick?.(),
                hotkey: "enter",
              },
            ]}
          />
        }
      >
        <NodeList
          {...Objects.FinancialAccountObject.getReferenceValueOptions(
            entityIdentifiers,
          )}
          fillHeight={false}
          // An account cannot be merged into itself, nor into another
          // entity's books; and a Stripe balance is not a bank account at
          // all - it is a different kind of record entirely.
          filterOptions={(options: FinancialAccountFragment[]) =>
            options.filter(
              (option) =>
                option?.__typename === "FinancialAccountObject" &&
                option?.identifier !== sourceIdentifier &&
                (!context?.entityIdentifier ||
                  option?.financialInstitution?.entity?.identifier ===
                    context.entityIdentifier),
            )
          }
          onSelectOption={(account: FinancialAccountFragment) =>
            setTarget(account)
          }
          onActiveIndexChange={(
            _account: FinancialAccountFragment,
            onClick: () => void,
          ) => {
            activeIndexRef.current.onClick = onClick;
          }}
          className="max-w-none"
        />
      </StickyLayout>
    );
  }

  return (
    <StickyLayout
      stickyBottomChildren={
        <Buttons
          buttons={[
            {
              variant: "outline",
              children: "Back",
              onClick: () => setTarget(null),
            },
            {
              variant: "default",
              children: <>{isMerging && <Spinner />}Merge</>,
              disabled: isMerging,

              onClick: () => {
                if (!sourceIdentifier) return;
                mergeAccounts({
                  variables: {
                    input: {
                      sourceIdentifier,
                      targetIdentifier: target.identifier,
                    },
                  },
                });
              },
            },
          ]}
        />
      }
    >
      <Typography level="body-md" className="p-4">
        {`Everything under ${sourceName} — its transactions, statements and tags — moves to ${target.name}, and ${sourceName} is removed. A line both accounts already hold is kept once rather than counted twice.`}
      </Typography>
    </StickyLayout>
  );
}

export function MergeBankAccountsCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Merge bank account",
      getLabel: () => "Merge",
      description: "Fold a duplicate bank account into the one to keep",
      component: ({ commandInstanceId, context }) => (
        <Component
          commandInstanceId={commandInstanceId}
          context={context as MergeBankAccountsContextType}
        />
      ),
      ReactIcon: MergeIcon,
      isHidden: true,
    });
    return () => unregisterCommandDefinition(COMMAND_ID);
  }, []);

  return null;
}
